import { Phone, Trash2, Mail, Save } from 'lucide-react'
import { Button } from '@/components/ds'
import { AdvancedLocationSearch } from 'components/LocationSearch/AdvancedLocationSearch'
import { LocationSearchExample } from 'components/LocationSearch/LocationSearchExample'
import { SockerIoDisconnectReason } from 'context/types'
import { useAuth } from 'hooks/auth'
import { useWebSocketRedux } from 'hooks/useWebSocketRedux'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { addNotification } from 'store/notificationSlice'
import { Category } from 'hooks/useNotification'
import { disconnected } from 'store/webSocketSlice'

const inputClasses =
    'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/20 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500'

// Local storage keys
export const LS_PHONE_KEY = 'numeo_dev_phone'
export const LS_EMAIL_KEY = 'numeo_dev_email'

export const DevelopersSettings: React.FC = () => {
    const { debugData, updateDebugData } = useAuth()
    const { reconnect: reconnectWs } = useWebSocketRedux()
    const dispatch = useDispatch()

    const [phoneNumberTest, setPhoneNumberTest] = useState<string>('')
    const [emailTest, setEmailTest] = useState<string>('')
    const [storageUsage, setStorageUsage] = useState<string>('0 KB')

    // Storage usage monitoring
    useEffect(() => {
        const checkStorage = async () => {
            try {
                const estimate = await navigator.storage.estimate()
                setStorageUsage(`${Math.round((estimate.usage || 0) / 1024)} KB / ${Math.round((estimate.quota || 0) / 1024)} KB`)
            } catch (error) {
                console.error('Storage estimate failed:', error)
            }
        }

        checkStorage()
        const interval = setInterval(checkStorage, 5000)
        return () => clearInterval(interval)
    }, [])

    useEffect(() => {
        const savedPhone = localStorage.getItem(LS_PHONE_KEY)
        const savedEmail = localStorage.getItem(LS_EMAIL_KEY)

        if (savedPhone !== null) {
            setPhoneNumberTest(savedPhone)
        } else if (debugData.spotFinder.mockBrokerPhoneNumber) {
            setPhoneNumberTest(debugData.spotFinder.mockBrokerPhoneNumber)
        }

        if (savedEmail !== null) {
            setEmailTest(savedEmail)
        } else if (debugData.spotFinder.mockBrokerEmail) {
            setEmailTest(debugData.spotFinder.mockBrokerEmail)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Save values to localStorage and update app state
    const saveValues = useCallback(() => {
        try {
            // Save to localStorage
            if (phoneNumberTest) {
                localStorage.setItem(LS_PHONE_KEY, phoneNumberTest)
            } else {
                localStorage.removeItem(LS_PHONE_KEY)
            }

            if (emailTest) {
                localStorage.setItem(LS_EMAIL_KEY, emailTest)
            } else {
                localStorage.removeItem(LS_EMAIL_KEY)
            }

            updateDebugData({
                ...debugData,
                spotFinder: {
                    ...debugData.spotFinder,
                    mockBrokerPhoneNumber: phoneNumberTest,
                    mockBrokerEmail: emailTest,
                },
            })

            dispatch(
                addNotification({
                    id: Date.now().toString(),
                    message: 'Developer settings saved successfully',
                    type: 'info',
                    topic: 'temp',
                    timestamp: Date.now(),
                    category: Category.Settings,
                })
            )
        } catch (error) {
            console.error('Failed to save developer settings:', error)
            dispatch(
                addNotification({
                    id: Date.now().toString(),
                    message: 'Failed to save developer settings',
                    type: 'error',
                    topic: 'temp',
                    timestamp: Date.now(),
                    category: Category.Settings,
                })
            )
        }
    }, [debugData, emailTest, phoneNumberTest, updateDebugData, dispatch])

    // Clear all values
    const clearValues = () => {
        // Clear phone number
        setPhoneNumberTest('')
        localStorage.removeItem(LS_PHONE_KEY)

        // Clear email
        setEmailTest('')
        localStorage.removeItem(LS_EMAIL_KEY)

        updateDebugData({
            ...debugData,
            spotFinder: {
                ...debugData.spotFinder,
                mockBrokerPhoneNumber: '',
                mockBrokerEmail: '',
            },
        })
    }

    return (
        <div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="h-full w-full rounded-lg border border-slate-300 p-4 dark:border-slate-600">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Spot Finder</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                        This section allows you to simulate calls to a mock broker. Enter a phone number in the field below to set the phone number that will be
                        used for testing call functionality without making actual calls.
                    </p>
                    <div className="mt-3 flex flex-col gap-3">
                        <div className="flex items-center gap-3">
                            <div className="relative max-w-[200px]">
                                <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                <input
                                    className={`${inputClasses} pl-9`}
                                    placeholder="Phone number"
                                    value={phoneNumberTest}
                                    onChange={(e) => setPhoneNumberTest(e.target.value)}
                                />
                            </div>
                            <div className="relative flex-1">
                                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                <input
                                    className={`${inputClasses} pl-9`}
                                    placeholder="Email"
                                    value={emailTest}
                                    onChange={(e) => setEmailTest(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <Button size="sm" variant="secondary" icon={<Save className="h-4 w-4" />} onClick={saveValues}>
                                Save
                            </Button>
                            <Button size="sm" variant="danger-soft" icon={<Trash2 className="h-4 w-4" />} onClick={clearValues}>
                                Clear
                            </Button>
                        </div>
                    </div>
                </div>
                <div className="h-full w-full rounded-lg border border-slate-300 p-4 dark:border-slate-600">
                    {/* Web Socket Section */}
                    <div className="flex items-center gap-3">
                        <h3 className="flex-1 text-lg font-semibold text-slate-900 dark:text-slate-100">Web Socket</h3>
                        <Button
                            variant="secondary"
                            onClick={() => {
                                // First disconnect the socket
                                dispatch(disconnected(SockerIoDisconnectReason.FORCE_DISCONNECT))

                                // Then immediately reconnect it, but with a delay
                                setTimeout(() => {
                                    console.log('Testing reconnection...')
                                    reconnectWs()
                                }, 1500) // Increased delay to 1.5 seconds
                            }}
                        >
                            Test Reconnect
                        </Button>
                    </div>
                    <textarea
                        className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/20 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                        placeholder="Web data"
                    />
                </div>
                <div className="h-full w-full rounded-lg border border-slate-300 dark:border-slate-600">
                    <div className="w-full p-4">
                        <LocationSearchExample />
                    </div>
                </div>
                <div className="h-full w-full rounded-lg border border-slate-300 dark:border-slate-600">
                    <div className="w-full p-4">
                        <AdvancedLocationSearch />
                        <div className="storage-monitor">
                            <span>IndexedDB Usage:</span>
                            <span>{storageUsage}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default DevelopersSettings
